import { useState, useCallback } from "react";
import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ApiAIImageGenerationBackend } from "@incendium/api";
import { editImage } from "features/ai/services/aiService";
import { useSelectedProject } from "Hooks/useSelectedProject";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { ReactComponent as AiIcon } from "Assets/icons/menu/ai.svg";

const CONTEXT_BACKEND =
  ApiAIImageGenerationBackend.AI_IMAGE_GENERATION_BACKEND_GEMINI_CONTEXT;

interface AIEditImageDialogProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  onEdited: (base64DataUrl: string) => void;
}

export default function AIEditImageDialog({
  open,
  onClose,
  imageUrl,
  onEdited,
}: AIEditImageDialogProps) {
  const { selectedProject } = useSelectedProject();
  const projectId = selectedProject?.id;
  const [prompt, setPrompt] = useState("");
  const [editing, setEditing] = useState(false);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const theme = useTheme();

  const handleApply = useCallback(async () => {
    if (!projectId || !prompt.trim()) return;
    setEditing(true);
    setPreviewSrc(null);
    try {
      const res = await editImage(projectId, {
        prompt: prompt.trim(),
        url: imageUrl,
        backend: CONTEXT_BACKEND,
      });
      if (res) {
        setPreviewSrc(`data:image/png;base64,${res}`);
      }
    } finally {
      setEditing(false);
    }
  }, [projectId, prompt, imageUrl]);

  const handleUseImage = useCallback(() => {
    if (previewSrc) {
      onEdited(previewSrc);
      onClose();
    }
  }, [previewSrc, onEdited, onClose]);

  const handleRevert = useCallback(() => {
    setPreviewSrc(null);
    setPrompt("");
  }, []);

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle><AiIcon width={20} height={20} /> Edit image with AI</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            mt: 1,
            minHeight: 320,
          }}
        >
          <Box
            sx={{
              flex: "1 1 50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === "light" ? "grey.50" : "grey.900",
              overflow: "hidden",
            }}
          >
            {previewSrc ? (
              <Box
                component="img"
                src={previewSrc}
                alt="Edited preview"
                sx={{
                  maxWidth: "100%",
                  maxHeight: 320,
                  objectFit: "contain",
                }}
              />
            ) : imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt="Current"
                sx={{
                  maxWidth: "100%",
                  maxHeight: 320,
                  objectFit: "contain",
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Preview will appear here
              </Typography>
            )}
          </Box>
          <Stack spacing={2} sx={{ flex: "1 1 50%", minWidth: 0 }}>
            {previewSrc ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Prompt
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {prompt}
                </Typography>
                <Button variant="contained" onClick={handleUseImage}>
                  Use this image
                </Button>
                <Button variant="outlined" onClick={handleRevert}>
                  Revert
                </Button>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Edit prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the changes you want..."
                  multiline
                  minRows={3}
                />
                <Button
                  variant="contained"
                  onClick={handleApply}
                  disabled={!prompt.trim() || !projectId || editing}
                >
                  {editing ? "Applying…" : "Apply"}
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </DialogContent>
    </CustomDialog>
  );
}
